/**
 * CODES PROMO — logique PURE de validation + calcul de réduction (US promo).
 * 100 % pur → testable sans réseau. C'est la SOURCE DE VÉRITÉ des règles ; le
 * serveur (RPC Supabase `validate_promo_code` / `redeem_promo_code`) applique les
 * MÊMES règles de façon autoritaire et atomique (jamais confiance au navigateur).
 *
 * Règles (spec) : refuser si inactif / hors dates / mauvais resto / total < min /
 * plafond d'utilisations atteint / (réservé 1re commande ET le client a déjà commandé)
 * / déjà utilisé par ce profil. Sinon appliquer la réduction (%/montant, plafonnée).
 */

export type PromoType = 'percent' | 'amount';

/** Un code promo tel que stocké (table promo_codes). */
export interface PromoCode {
  id: string;
  tenant_id: string;
  code: string;
  type: PromoType;
  /** percent : 0–100 (ex. 20 = -20%) ; amount : centimes (ex. 300 = -3€). */
  value: number;
  min_order_cents?: number | null;
  max_discount_cents?: number | null;
  first_order_only?: boolean | null;
  starts_at?: string | null;
  ends_at?: string | null;
  max_redemptions?: number | null;
  active?: boolean | null;
}

/** Contexte d'évaluation (données dépendantes du client/commande, fournies par le serveur). */
export interface PromoContext {
  tenantId: string;
  /** Montant sur lequel s'applique la remise (sous-total avant remise), en centimes. */
  subtotalCents: number;
  now?: Date;
  /** Ce profil (téléphone) a-t-il déjà utilisé CE code ? (unicité par profil) */
  alreadyRedeemedByProfile?: boolean;
  /** Nombre total d'utilisations déjà consommées (plafond global). */
  totalRedemptions?: number;
  /** Le client a-t-il déjà passé une commande en ligne ? (option « réservé 1re commande »). */
  customerHasOnlineOrder?: boolean;
}

export type PromoRejectReason =
  | 'inactive'
  | 'wrong_tenant'
  | 'not_started'
  | 'expired'
  | 'min_order'
  | 'max_redemptions'
  | 'first_order_only'
  | 'already_used';

export type PromoResult =
  | { ok: true; discountCents: number; finalCents: number }
  | { ok: false; reason: PromoRejectReason };

/** Normalise un code saisi : majuscules, sans espaces (comparaison robuste). */
export function normalizePromoCode(raw: string | null | undefined): string {
  return (raw ?? '').trim().toUpperCase().replace(/\s+/g, '');
}

/** Clé de profil anti double-usage : 9 derniers chiffres du téléphone (parité matching FR/CH). */
export function phoneProfileKey(phone: string | null | undefined): string {
  const digits = (phone ?? '').replace(/\D/g, '');
  return digits.slice(-9);
}

/**
 * Réduction en centimes pour un code + un sous-total : %/montant, plafonnée par
 * `max_discount_cents`, jamais négative ni supérieure au sous-total.
 */
export function computeDiscountCents(code: Pick<PromoCode, 'type' | 'value' | 'max_discount_cents'>, subtotalCents: number): number {
  const base = Math.max(0, Math.floor(subtotalCents));
  let d =
    code.type === 'percent'
      ? Math.round((base * Math.max(0, code.value)) / 100)
      : Math.round(Math.max(0, code.value));
  if (code.max_discount_cents != null) d = Math.min(d, Math.max(0, code.max_discount_cents));
  return Math.max(0, Math.min(d, base));
}

/**
 * Valide un code dans un contexte donné et calcule la remise.
 * Ordre des refus : inactif → mauvais resto → dates → minimum → plafond global →
 * réservé 1re commande → déjà utilisé par le profil.
 */
export function validatePromo(code: PromoCode, ctx: PromoContext): PromoResult {
  const now = ctx.now ?? new Date();
  if (!code.active) return { ok: false, reason: 'inactive' };
  if (code.tenant_id !== ctx.tenantId) return { ok: false, reason: 'wrong_tenant' };
  if (code.starts_at && now < new Date(code.starts_at)) return { ok: false, reason: 'not_started' };
  if (code.ends_at && now > new Date(code.ends_at)) return { ok: false, reason: 'expired' };
  if (code.min_order_cents != null && ctx.subtotalCents < code.min_order_cents) {
    return { ok: false, reason: 'min_order' };
  }
  if (code.max_redemptions != null && (ctx.totalRedemptions ?? 0) >= code.max_redemptions) {
    return { ok: false, reason: 'max_redemptions' };
  }
  if (code.first_order_only && ctx.customerHasOnlineOrder) {
    return { ok: false, reason: 'first_order_only' };
  }
  if (ctx.alreadyRedeemedByProfile) return { ok: false, reason: 'already_used' };

  const discountCents = computeDiscountCents(code, ctx.subtotalCents);
  return { ok: true, discountCents, finalCents: Math.max(0, ctx.subtotalCents - discountCents) };
}

/** Message clair pour le client selon le motif de refus. */
export function promoRejectionMessage(reason: PromoRejectReason): string {
  switch (reason) {
    case 'inactive':
      return 'Ce code promo n’est pas valide.';
    case 'wrong_tenant':
      return 'Ce code n’est pas valable dans ce restaurant.';
    case 'not_started':
      return 'Ce code n’est pas encore actif.';
    case 'expired':
      return 'Ce code promo a expiré.';
    case 'min_order':
      return 'Montant de commande insuffisant pour ce code.';
    case 'max_redemptions':
      return 'Ce code a atteint son nombre maximum d’utilisations.';
    case 'first_order_only':
      return 'Ce code est réservé à une première commande en ligne.';
    case 'already_used':
      return 'Vous avez déjà utilisé ce code.';
    default:
      return 'Code promo invalide.';
  }
}
